import { useEffect, useSyncExternalStore } from 'react';
import { apiGet, apiPost } from '@/lib/api';
import { isIos, isStandalone } from '@/lib/pwa';
import { useSession } from '@/features/auth/use-session';

export type PushPermission = NotificationPermission | 'unsupported';

type PushState = {
  permission: PushPermission;
  /** A subscription exists on this device and the server has been told about it. */
  enabled: boolean;
  busy: boolean;
};

/** Wire shape for POST /notifications/push/subscriptions. */
type SubscriptionBody = {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  userAgent?: string;
};

/** A setup failure with a message fit for the screen, unlike a raw DOMException. */
export class PushSetupError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PushSetupError';
  }
}

/**
 * Push needs the worker (production only, see registerServiceWorker), the Push
 * API and the Notification API. iOS Safari in a tab has none of them; it grows
 * them once the site is added to the Home Screen.
 */
const supported =
  import.meta.env.PROD &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

// ── Shared store ─────────────────────────────────────────────────────────────
// One state for every mounted hook, so turning push on from the account page
// also updates the prompt card in the shell.

let state: PushState = {
  permission: supported ? Notification.permission : 'unsupported',
  enabled: false,
  busy: false,
};
const listeners = new Set<() => void>();

function setState(patch: Partial<PushState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): PushState {
  return state;
}

if (supported && 'permissions' in navigator) {
  // Reflect a change made in the browser's site settings while the page is open.
  navigator.permissions
    .query({ name: 'notifications' })
    .then((status) => {
      status.onchange = () => {
        const permission = Notification.permission;
        setState({ permission, enabled: permission === 'granted' && state.enabled });
      };
    })
    .catch(() => undefined);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** VAPID keys travel as URL-safe base64; the Push API wants the raw bytes. */
function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padded = `${value}${'='.repeat((4 - (value.length % 4)) % 4)}`;
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

/** The active registration, or null when there is none (dev builds). */
async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (!existing) return null;
  return navigator.serviceWorker.ready;
}

function toBody(subscription: PushSubscription): SubscriptionBody | null {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh, auth }, userAgent: navigator.userAgent };
}

async function subscribePush(
  registration: ServiceWorkerRegistration,
  publicKey: string,
): Promise<PushSubscription> {
  const options: PushSubscriptionOptionsInit = {
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  };
  try {
    return await registration.pushManager.subscribe(options);
  } catch (caught) {
    // A subscription made under an earlier VAPID key blocks a new one; replace it.
    const stale = await registration.pushManager.getSubscription();
    if (!stale) throw caught;
    await stale.unsubscribe();
    return registration.pushManager.subscribe(options);
  }
}

/**
 * Tells the API about a subscription once per endpoint per page load. The
 * server upserts by endpoint, so repeating it after a reinstall is harmless,
 * but two hooks mounting together should still produce a single request.
 */
let sync: { endpoint: string; promise: Promise<void> } | null = null;

function syncSubscription(body: SubscriptionBody): Promise<void> {
  if (sync?.endpoint !== body.endpoint) {
    const promise = apiPost<{ id: string }>('/notifications/push/subscriptions', body).then(
      () => undefined,
      (caught: unknown) => {
        sync = null; // so the next attempt is a real request
        throw caught;
      },
    );
    sync = { endpoint: body.endpoint, promise };
  }
  return sync.promise;
}

// ── Actions ──────────────────────────────────────────────────────────────────

/**
 * Asks for permission, subscribes and registers the device. Resolves false
 * when the user did not grant permission; throws (ApiRequestError or
 * PushSetupError) when something else stood in the way.
 */
async function enable(): Promise<boolean> {
  if (!supported || state.busy) return false;
  setState({ busy: true });
  try {
    // First, with nothing awaited before it: browsers only show the permission
    // dialog while the click's user activation is still live.
    const permission = await Notification.requestPermission();
    setState({ permission });
    if (permission !== 'granted') return false;

    const registration = await getRegistration();
    if (!registration) {
      throw new PushSetupError('The app is still installing on this device. Reload and try again.');
    }
    const { publicKey } = await apiGet<{ publicKey: string | null }>(
      '/notifications/push/public-key',
    );
    if (!publicKey) throw new PushSetupError('Push notifications are not set up on the server.');

    const body = toBody(await subscribePush(registration, publicKey));
    if (!body) throw new PushSetupError('This browser did not return a usable subscription.');
    sync = null; // force a fresh request even if this endpoint was seen before
    await syncSubscription(body);
    setState({ enabled: true });
    return true;
  } finally {
    setState({ busy: false });
  }
}

/** Drops the local subscription and tells the API to forget it. */
async function disable(): Promise<void> {
  if (!supported || state.busy) return;
  setState({ busy: true });
  try {
    const registration = await getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      await apiPost<{ removed: number }>('/notifications/push/subscriptions/remove', {
        endpoint: subscription.endpoint,
      });
    }
    sync = null;
    setState({ enabled: false });
  } finally {
    setState({ busy: false });
  }
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Browser push for this device. `enabled` means a subscription exists here and
 * the server knows about it; `permission` is the browser's answer, or
 * "unsupported" where push cannot work at all.
 */
export function usePushNotifications() {
  const { permission, enabled, busy } = useSyncExternalStore(subscribe, getSnapshot);
  const { data: user } = useSession();
  const signedIn = Boolean(user);

  // Keep the server current after a reinstall or a cleared database: re-post an
  // existing subscription once per page load, and only while signed in.
  useEffect(() => {
    if (!supported || !signedIn || Notification.permission !== 'granted') return;
    void (async () => {
      const registration = await getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      const body = subscription ? toBody(subscription) : null;
      if (!body) return;
      await syncSubscription(body);
      setState({ enabled: true });
    })().catch(() => undefined);
  }, [signedIn]);

  return {
    supported,
    permission,
    enabled,
    busy,
    enable,
    disable,
    /** iOS only grants push to Home Screen apps, so a Safari tab must install first. */
    needsInstallOnIos: isIos() && !isStandalone(),
  };
}
